import { useEffect, useRef } from "react";

const lines = [
  "音が、境界を壊す。",
  "光が、形を溶かす。",
  "身体が、感覚を取り戻す。",
];

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Ripple wave canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* concentric wave rings from left-centre — sound ripple */
      const ox = W * 0.08;
      const oy = H * 0.5;
      for (let ring = 0; ring < 8; ring++) {
        const phase = ((t * 0.006 + ring * 0.125) % 1);
        const r = phase * Math.max(W, H) * 0.9;
        const alpha = (1 - phase) * 0.07;
        ctx.beginPath();
        ctx.arc(ox, oy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* sine wave lines — sound waveform */
      for (let wave = 0; wave < 4; wave++) {
        ctx.beginPath();
        const amp = (H * 0.04) * (1 - wave * 0.18);
        const freq = 0.012 + wave * 0.004;
        const phaseShift = t * 0.018 + wave * 0.8;
        const alpha = 0.06 - wave * 0.012;
        for (let x = 0; x <= W; x += 2) {
          const y = H * 0.5 + Math.sin(x * freq + phaseShift) * amp * Math.sin((x / W) * Math.PI);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* floating particles */
      for (let p = 0; p < 30; p++) {
        const px = ((Math.sin(t * 0.007 + p * 1.3) * 0.5 + 0.5) * W);
        const py = ((Math.cos(t * 0.005 + p * 0.9) * 0.5 + 0.5) * H);
        const alpha = (Math.sin(t * 0.02 + p) * 0.5 + 0.5) * 0.12;
        ctx.beginPath();
        ctx.arc(px, py, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,220,140,${alpha})`;
        ctx.fill();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll(".exp-line");
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
                (el as HTMLElement).style.filter = "blur(0px)";
              }, i * 380);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="relative w-full flex items-center overflow-hidden"
      style={{ background: "#030303" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-44">
        {/* section label */}
        <div className="flex items-center gap-4 mb-16 md:mb-24">
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
          <p className="text-white/15 tracking-[0.5em] uppercase text-xs" style={{ fontFamily: "'Courier New', monospace" }}>
            experience
          </p>
        </div>

        <div className="flex flex-col gap-10 md:gap-0 md:grid md:grid-cols-2 md:gap-32 items-start md:items-center">
          <div className="flex flex-col gap-8 md:gap-16">
            {lines.map((line, i) => (
              <div key={i} className="exp-line flex items-start gap-4"
                style={{
                  opacity: 0,
                  transform: "translateY(24px)",
                  filter: "blur(6px)",
                  transition: "opacity 1.8s ease, transform 1.8s ease, filter 1.8s ease",
                }}
              >
                {/* waveform bar */}
                <div className="flex-shrink-0 flex flex-col justify-center gap-[3px] mt-2" style={{ width: "14px" }}>
                  {[1, 0.6, 0.85, 0.4, 0.7].map((h, j) => (
                    <div
                      key={j}
                      style={{
                        width: "2px",
                        height: `${h * 10}px`,
                        background: `rgba(255,220,140,${0.2 + h * 0.3})`,
                        borderRadius: "1px",
                      }}
                    />
                  ))}
                </div>
                <p
                  className="text-white font-light leading-tight"
                  style={{
                    fontSize: "clamp(20px, 3vw, 44px)",
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                    fontWeight: 300,
                    letterSpacing: "0.03em",
                    lineHeight: 1.5,
                  }}
                >
                  {line}
                </p>
              </div>
            ))}
          </div>

          <div
            className="exp-line flex flex-col gap-3 md:pl-8 mt-0"
            style={{
              opacity: 0,
              transform: "translateY(24px)",
              filter: "blur(6px)",
              transition: "opacity 1.8s ease, transform 1.8s ease, filter 1.8s ease",
            }}
          >
            {/* decorative vertical line */}
            <div style={{ width: "1px", height: "48px", background: "linear-gradient(to bottom, rgba(255,220,140,0.3), transparent)", marginBottom: "12px" }} />
            <p
              className="text-white/40 font-light leading-loose"
              style={{
                fontSize: "clamp(16px, 1.6vw, 20px)",
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontWeight: 300,
                letterSpacing: "0.08em",
                lineHeight: 2,
              }}
            >
              ただ、
            </p>
            <p
              className="text-white/40 font-light leading-loose"
              style={{
                fontSize: "clamp(16px, 1.6vw, 20px)",
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontWeight: 300,
                letterSpacing: "0.08em",
                lineHeight: 2,
              }}
            >
              在るだけでいい。
            </p>
            <div style={{ width: "1px", height: "48px", background: "linear-gradient(to bottom, transparent, rgba(255,220,140,0.15))", marginTop: "12px" }} />
          </div>
        </div>
      </div>
    </section>
  );
}
